// src/pages/admin/User_department.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../../api/users/departments";
import { listCompanies } from "../../api/users/companies";

const EMPTY_DEPT = { name: "", company: "" }; // company는 ID 저장

const User_department = () => {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newDept, setNewDept] = useState({ ...EMPTY_DEPT });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ ...EMPTY_DEPT });

  // 검색 & 페이징
  const [q, setQ] = useState("");
  const [companyFilter, setCompanyFilter] = useState(""); // ▶ 회사 필터 추가
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [count, setCount] = useState(0);
  const totalPages = useMemo(
    () => (count > 0 ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  // 회사별 그룹 보기용 데이터
  const [grouped, setGrouped] = useState({});
  const [groupOpen, setGroupOpen] = useState({}); // 각 회사별 접힘/펼침

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [page, pageSize, q, companyFilter]);

  // 그룹 보기 데이터는 필터/검색에 덜 민감하게 "한 번에" 보도록 별도 로드
  useEffect(() => {
    loadGroupedDepartments();
  }, [q]); // 필요시 companyFilter 포함 가능

  const loadCompanies = async () => {
    try {
      const res = await listCompanies();
      const data = res?.data ?? res;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setCompanies(rows);
    } catch (error) {
      console.error("회사 목록 불러오기 실패:", error);
    }
  };

  const parseListResponse = (data) => {
    const payload = data?.data ?? data;
    if (Array.isArray(payload)) {
      setRows(payload);
      setCount(payload.length);
    } else {
      setRows(payload?.results ?? []);
      setCount(payload?.count ?? payload?.results?.length ?? 0);
    }
  };

  const loadDepartments = async () => {
    try {
      const res = await listDepartments({
        page,
        page_size: pageSize,
        search: q || undefined,
        company: companyFilter || undefined, // ▶ 회사 필터 적용 (백엔드에서 지원)
      });
      parseListResponse(res);
    } catch (e) {
      console.error("부서 목록 불러오기 실패:", e);
    }
  };

  // 회사별 그룹 데이터 로드 (페이지 없이 넉넉히)
  const loadGroupedDepartments = async () => {
    try {
      const res = await listDepartments({
        page: 1,
        page_size: 9999,
        search: q || undefined,
      });
      const payload = res?.data ?? res;
      const all = Array.isArray(payload) ? payload : payload?.results ?? [];
      // 회사별로 묶기
      const map = {};
      all.forEach((r) => {
        const key =
          r.company_name ??
          (typeof r.company === "object" ? r.company?.name : r.company) ??
          "(회사없음)";
        if (!map[key]) map[key] = [];
        map[key].push(r);
      });
      setGrouped(map);
      // 기본은 모두 펼쳐진 상태로
      const openInit = {};
      Object.keys(map).forEach((k) => (openInit[k] = true));
      setGroupOpen(openInit);
    } catch (e) {
      console.error("회사별 부서 로드 실패:", e);
    }
  };

  // 등록
  const handleCreate = async () => {
    if (!newDept.name.trim()) {
      alert("부서명을 입력하세요.");
      return;
    }
    if (!newDept.company) {
      alert("회사를 선택하세요.");
      return;
    }
    try {
      await createDepartment(newDept);
      setNewDept({ ...EMPTY_DEPT });
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
    } catch (e) {
      console.error("부서 등록 실패:", e);
    }
  };

  // 수정
  const handleUpdate = async (id) => {
    if (!editData.name.trim()) {
      alert("부서명을 입력하세요.");
      return;
    }
    if (!editData.company) {
      alert("회사를 선택하세요.");
      return;
    }
    try {
      await updateDepartment(id, editData);
      setEditId(null);
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
    } catch (e) {
      console.error("부서 수정 실패:", e);
    }
  };

  // 삭제
  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deleteDepartment(id);
      await Promise.all([loadDepartments(), loadGroupedDepartments()]);
    } catch (e) {
      console.error("부서 삭제 실패:", e);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditData({
      name: row.name ?? "",
      // listDepartments 응답이 company id를 주면 그대로, 객체면 id 추출
      company:
        typeof row.company === "object"
          ? row.company?.id ?? ""
          : row.company ?? "",
    });
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4">직무/소속 부서 관리</h2>

      {/* 상단 검색/필터 */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="검색(부서명)"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          className="border rounded px-3 py-2 w-60"
        />
        <select
          value={companyFilter}
          onChange={(e) => {
            setPage(1);
            setCompanyFilter(e.target.value);
          }}
          className="border rounded px-3 py-2 bg-white"
        >
          <option value="">전체 회사</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          onClick={() => {
            setQ("");
            setCompanyFilter("");
            setPage(1);
          }}
          className="px-3 py-2 border rounded"
        >
          초기화
        </button>
      </div>

      {/* 등록 */}
      <fieldset className="border border-gray-300 rounded p-4 mb-6">
        <legend className="text-lg font-semibold">부서 등록</legend>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <input
            type="text"
            placeholder="부서명 *"
            value={newDept.name}
            onChange={(e) =>
              setNewDept((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
            className="border rounded px-3 py-2 w-full"
          />
          <select
            value={newDept.company}
            onChange={(e) =>
              setNewDept((prev) => ({
                ...prev,
                company: Number(e.target.value),
              }))
            }
            className="border rounded px-3 py-2 w-full bg-white"
          >
            <option value="">회사 선택 *</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4">
          <button
            onClick={handleCreate}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            등록
          </button>
        </div>
      </fieldset>

      {/* 목록 */}
      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1 w-16">ID</th>
              <th className="border px-2 py-1">부서명</th>
              <th className="border px-2 py-1">회사</th>
              <th className="border px-2 py-1 w-40">관리</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="border px-2 py-1 text-center">{r.id}</td>
                <td className="border px-2 py-1">
                  {editId === r.id ? (
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="border rounded px-2 py-1 w-full"
                    />
                  ) : (
                    r.name
                  )}
                </td>
                <td className="border px-2 py-1">
                  {editId === r.id ? (
                    <select
                      value={editData.company}
                      onChange={(e) =>
                        setEditData((prev) => ({
                          ...prev,
                          company: Number(e.target.value),
                        }))
                      }
                      className="border rounded px-2 py-1 w-full bg-white"
                    >
                      <option value="">회사 선택 *</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    r.company_name ?? "-"
                  )}
                </td>
                <td className="border px-2 py-1">
                  {editId === r.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          handleUpdate(r.id, {
                            ...editData,
                            company: Number(editData.company),
                          })
                        }
                        className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => setEditId(null)}
                        className="bg-gray-400 hover:bg-gray-500 text-white px-2 py-1 rounded"
                      >
                        취소
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(r)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                      >
                        삭제
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-4">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="px-3 py-2 border rounded disabled:opacity-40"
        >
          이전
        </button>
        <span className="text-sm">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
          disabled={page >= totalPages}
          className="px-3 py-2 border rounded disabled:opacity-40"
        >
          다음
        </button>
      </div>

      {/* 회사별 부서 한눈에 */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">회사별 부서 한눈에</h3>
          <button
            onClick={loadGroupedDepartments}
            className="px-3 py-2 border rounded"
          >
            새로고침
          </button>
        </div>

        <div className="space-y-3">
          {Object.keys(grouped).length === 0 && (
            <div className="text-gray-500 text-sm">데이터가 없습니다.</div>
          )}
          {Object.entries(grouped).map(([companyName, items]) => (
            <div key={companyName} className="border rounded">
              <button
                onClick={() =>
                  setGroupOpen((s) => ({
                    ...s,
                    [companyName]: !s[companyName],
                  }))
                }
                className="w-full text-left px-3 py-2 bg-gray-100 hover:bg-gray-200 flex justify-between"
              >
                <span className="font-medium">
                  {companyName}{" "}
                  <span className="text-gray-500 text-sm">
                    (부서 {items.length}개)
                  </span>
                </span>
                <span>{groupOpen[companyName] ? "▼" : "▲"}</span>
              </button>
              {groupOpen[companyName] && (
                <div className="px-3 py-2">
                  <ul className="list-disc pl-5">
                    {items
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((d) => (
                        <li key={d.id}>
                          {d.name}{" "}
                          <span className="text-gray-400 text-xs">#{d.id}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default User_department;
